import { useCallback, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { AlertCircle, CalendarDays, ChevronLeft, ChevronRight, Radio } from 'lucide-react';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { JobQuickViewSheet } from '@/components/jobs/JobQuickViewSheet';
import { JobsHeader } from '@/components/jobs/JobsHeader';
import { MoveConfirmDialog } from '@/components/schedule/MoveConfirmDialog';
import { FleetMap, type MapTech } from '@/components/map/FleetMap';
import { JobCard, TechCard, TechList, UnassignedList } from '@/components/map/FleetPanel';
import {
  fromMinutes, jobDayKey, jobWindow, moveChangesAnything, shiftDay, techColour,
  type ScheduleJob, type ScheduleMove,
} from '@/components/schedule/scheduleModel';
import { useScheduleJobs, useScheduleMove } from '@/hooks/useSchedule';
import { useTechsWithMetros, useMetros } from '@/hooks/useReferenceData';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';
import { useUserRole } from '@/hooks/useUserRole';
import { useFleetView } from '@/hooks/useFleetView';
import { useTechTrail } from '@/hooks/useFleetLive';
import { useMapAreas } from '@/hooks/useMapAreas';
import { hoursSummary, type FleetStatus } from '@/utils/fleetStatus';
import { formatInTz } from '@/utils/timezone';

const UNASSIGNED_COLOUR = 'hsl(var(--muted-foreground))';
const isDayKey = (v: string | null): v is string => !!v && /^\d{4}-\d{2}-\d{2}$/.test(v);

type Selection = { kind: 'job'; id: string } | { kind: 'tech'; id: string } | null;
type Layer = 'techs' | 'jobs' | 'areas';

/** A technician on a day other than today: their jobs, no live position. */
function dayOnlyStatus(jobCount: number, future: boolean): FleetStatus {
  return {
    activity: jobCount ? 'between_jobs' : 'no_jobs',
    activityLabel: jobCount ? `${jobCount} job${jobCount === 1 ? '' : 's'}` : 'No jobs',
    activeJob: null,
    nextJob: null,
    eta: null,
    location: 'off_hours',
    locationLabel: future ? 'Live position shows on the day' : 'Live position only shows for today',
    tone: 'muted',
    position: null,
    stale: false,
  };
}

export default function LiveMap() {
  const { locale, timezone } = useCompanyLocale();
  const { isAdmin, isCSR, isLoading: rolesLoading } = useUserRole();
  const staff = isAdmin || isCSR;
  const [params, setParams] = useSearchParams();
  const today = formatInTz(new Date(), timezone, 'yyyy-MM-dd');
  const day = isDayKey(params.get('date')) ? (params.get('date') as string) : today;
  const isToday = day === today;

  const [metroId, setMetroId] = useState('all');
  const [layers, setLayers] = useState<Layer[]>(['techs', 'jobs']);
  const [selection, setSelection] = useState<Selection>(null);
  const [followTechId, setFollowTechId] = useState<string | null>(null);
  const [showTrail, setShowTrail] = useState(false);
  const [openJobId, setOpenJobId] = useState<string | null>(null);
  const [pendingMove, setPendingMove] = useState<ScheduleMove | null>(null);
  const [tab, setTab] = useState('techs');

  const setDay = (d: string) => {
    const p = new URLSearchParams(params);
    p.set('date', d);
    setParams(p, { replace: true });
    setFollowTechId(null);
  };

  const { data: jobs = [], isLoading, error, refetch } = useScheduleJobs(day, day, timezone);
  const { data: techs = [] } = useTechsWithMetros(true);
  const { data: metros = [] } = useMetros(true);
  const { areas, fallback } = useMapAreas();
  const move = useScheduleMove();

  // Same colours as the Schedule board: position in the active tech list.
  const colourIndex = useMemo(() => new Map(techs.map((t, i) => [t.id, i])), [techs]);
  const colourOf = useCallback(
    (techId: string | null) => (techId ? techColour(colourIndex.get(techId) ?? techs.length) : UNASSIGNED_COLOUR),
    [colourIndex, techs.length],
  );
  const techName = useCallback(
    (id: string | null) => (id ? techs.find((t) => t.id === id)?.name ?? 'Technician' : 'Unassigned'),
    [techs],
  );

  const dayJobs = useMemo(() => jobs.filter((j) => jobDayKey(j, timezone) === day), [jobs, timezone, day]);

  const fleet = useFleetView({ jobs: dayJobs, colourOf, enabled: staff && isToday });

  // Today: the live fleet. Other days: every active tech with that day's jobs, no position.
  const entries = useMemo<MapTech[]>(() => {
    if (isToday) return fleet.entries;
    return techs.map((t) => ({
      tech: { tech_id: t.id, name: t.name, phone: t.phone ?? null, tracking_enabled: true, notice_accepted: true },
      status: dayOnlyStatus(dayJobs.filter((j) => j.assigned_tech_id === t.id).length, day > today),
      colour: colourOf(t.id),
    }));
  }, [isToday, fleet.entries, techs, dayJobs, day, today, colourOf]);

  // Area filter: jobs in another area hide; techs who cover another area only hide.
  const inArea = useCallback(
    (techId: string) => {
      if (metroId === 'all') return true;
      const t = techs.find((x) => x.id === techId);
      return !t?.tech_metros?.length || t.tech_metros.some((m) => m.metro_id === metroId);
    },
    [metroId, techs],
  );
  const visibleJobs = useMemo(
    () => (metroId === 'all' ? dayJobs : dayJobs.filter((j) => !j.metro_id || j.metro_id === metroId)),
    [dayJobs, metroId],
  );
  const visibleEntries = useMemo(() => entries.filter((e) => inArea(e.tech.tech_id)), [entries, inArea]);
  const unassigned = useMemo(
    () => visibleJobs.filter((j) => !j.assigned_tech_id && j.status !== 'completed'),
    [visibleJobs],
  );

  const selectedJob = selection?.kind === 'job' ? dayJobs.find((j) => j.id === selection.id) ?? null : null;
  const selectedEntry = selection?.kind === 'tech' ? entries.find((e) => e.tech.tech_id === selection.id) ?? null : null;
  const trailTechId = isAdmin && showTrail && selectedEntry ? selectedEntry.tech.tech_id : null;
  const trail = useTechTrail(trailTechId, day, !!trailTechId);

  const selectTech = (id: string) => {
    setSelection({ kind: 'tech', id });
    if (followTechId && followTechId !== id) setFollowTechId(null);
  };
  const selectJob = (id: string) => setSelection({ kind: 'job', id });
  const clearSelection = () => {
    setSelection(null);
    setShowTrail(false);
    setFollowTechId(null);
  };

  const assign = (job: ScheduleJob, techId: string) => {
    const w = jobWindow(job);
    const next: ScheduleMove = {
      job,
      day: jobDayKey(job, timezone) ?? day,
      start: w ? fromMinutes(w.start) : null,
      end: w?.hasEnd ? fromMinutes(w.end) : null,
      techId,
    };
    if (moveChangesAnything(next, timezone)) setPendingMove(next);
  };

  const confirmMove = () => {
    if (!pendingMove) return;
    move.mutate(
      {
        jobId: pendingMove.job.id,
        jobNumber: pendingMove.job.job_number,
        day: pendingMove.day,
        start: pendingMove.start,
        end: pendingMove.end,
        techId: pendingMove.techId,
        techName: pendingMove.techId ? techName(pendingMove.techId) : null,
      },
      { onSettled: () => setPendingMove(null) },
    );
  };

  const snapshot = fleet.data;
  const onMap = visibleEntries.filter((e) => e.status.position).length;
  const unit = locale.distanceUnit;

  if (rolesLoading) {
    return (
      <Layout>
        <Skeleton className="h-[60vh] w-full" />
      </Layout>
    );
  }

  if (!staff) {
    return (
      <Layout>
        <div className="py-16 text-center text-muted-foreground">The map is for office staff.</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-4">
        <JobsHeader view="map" description="Where your technicians are and where the day’s jobs are." />

        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1">
            <Button variant="outline" size="icon" aria-label="Previous day" onClick={() => setDay(shiftDay(day, -1))}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" onClick={() => setDay(today)} disabled={isToday}>Today</Button>
            <Button variant="outline" size="icon" aria-label="Next day" onClick={() => setDay(shiftDay(day, 1))}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="ghost" className="gap-2 px-2 text-base font-semibold">
                <CalendarDays className="h-4 w-4" />
                {format(parseISO(day), 'EEEE d MMMM')}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={parseISO(day)}
                weekStartsOn={locale.weekStartsOn}
                onSelect={(d) => d && setDay(format(d, 'yyyy-MM-dd'))}
                initialFocus
              />
            </PopoverContent>
          </Popover>

          <div className="ml-auto flex flex-wrap items-center gap-2">
            {isToday && (
              <span
                className="flex items-center gap-1.5 text-xs text-muted-foreground"
                title={fleet.live ? 'Positions arrive as technicians report' : 'Refreshing every minute'}
              >
                <Radio className={fleet.live ? 'h-3.5 w-3.5 text-success' : 'h-3.5 w-3.5'} />
                {fleet.live ? 'Live' : 'Every minute'}
              </span>
            )}
            {metros.length > 1 && (
              <Select value={metroId} onValueChange={setMetroId}>
                <SelectTrigger className="w-[160px]">
                  <SelectValue placeholder="All areas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All areas</SelectItem>
                  {metros.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            <ToggleGroup
              type="multiple"
              variant="outline"
              value={layers}
              onValueChange={(v) => setLayers(v as Layer[])}
              aria-label="Map layers"
            >
              <ToggleGroupItem value="techs" className="px-3">Techs</ToggleGroupItem>
              <ToggleGroupItem value="jobs" className="px-3">Jobs</ToggleGroupItem>
              {areas.length > 0 && <ToggleGroupItem value="areas" className="px-3">Areas</ToggleGroupItem>}
            </ToggleGroup>
          </div>
        </div>

        {isToday && snapshot && !snapshot.tracking_enabled && (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-warning/40 bg-warning/10 px-3 py-2 text-sm">
            <span>Technician tracking is off, so only jobs are on the map.</span>
            {isAdmin
              ? <Link to="/settings?tab=tech-tracking" className="font-medium text-primary underline-offset-4 hover:underline">Turn it on in Settings</Link>
              : <span className="text-muted-foreground">An admin can turn it on in Settings.</span>}
          </div>
        )}
        {isToday && snapshot?.tracking_enabled && !snapshot.in_hours_now && (
          <p className="text-sm text-muted-foreground">
            Outside tracking hours ({hoursSummary(snapshot.hours)}), so technician positions are hidden.
          </p>
        )}
        {isToday && fleet.error && (
          <p className="flex items-center gap-2 text-sm text-warning">
            <AlertCircle className="h-4 w-4" />
            Technician positions are unavailable: {(fleet.error as Error).message}
          </p>
        )}

        {error ? (
          <div className="rounded-lg border py-12 text-center">
            <AlertCircle className="mx-auto mb-3 h-8 w-8 text-destructive" />
            <p className="mb-4 text-muted-foreground">{(error as Error).message}</p>
            <Button variant="outline" onClick={() => refetch()}>Try again</Button>
          </div>
        ) : (
          <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_360px]">
            <div className="h-[60vh] min-h-[420px] lg:h-[calc(100vh-15rem)]">
              <FleetMap
                jobs={layers.includes('jobs') ? visibleJobs : []}
                colourOf={colourOf}
                techs={layers.includes('techs') ? visibleEntries : []}
                trail={trailTechId ? trail.data : null}
                trailColour={selectedEntry?.colour}
                areas={layers.includes('areas') ? areas : []}
                selectedJobId={selectedJob?.id ?? null}
                selectedTechId={selectedEntry?.tech.tech_id ?? null}
                onSelectJob={selectJob}
                onSelectTech={selectTech}
                followTechId={followTechId}
                fallback={fallback}
                fitKey={`${day}|${metroId}`}
                gestureHandling="greedy"
                className="h-full"
              />
            </div>

            <div className="space-y-3 lg:max-h-[calc(100vh-15rem)] lg:overflow-y-auto">
              {selectedJob && (
                <JobCard
                  job={selectedJob}
                  entries={visibleEntries}
                  techName={techName}
                  unit={unit}
                  onOpen={() => setOpenJobId(selectedJob.id)}
                  onAssign={(techId) => assign(selectedJob, techId)}
                  onClose={clearSelection}
                />
              )}
              {selectedEntry && (
                <TechCard
                  entry={selectedEntry}
                  jobs={dayJobs.filter((j) => j.assigned_tech_id === selectedEntry.tech.tech_id)}
                  timezone={timezone}
                  canSeeTrail={isAdmin}
                  showTrail={showTrail}
                  trailPoints={trailTechId ? trail.data?.length ?? null : null}
                  following={followTechId === selectedEntry.tech.tech_id}
                  onToggleTrail={() => setShowTrail((s) => !s)}
                  onToggleFollow={() =>
                    setFollowTechId((f) => (f === selectedEntry.tech.tech_id ? null : selectedEntry.tech.tech_id))}
                  onOpenJob={selectJob}
                  onClose={clearSelection}
                />
              )}

              <Tabs value={tab} onValueChange={setTab}>
                <TabsList className="w-full">
                  <TabsTrigger value="techs" className="flex-1">
                    Technicians{isToday ? ` · ${onMap}/${visibleEntries.length} on map` : ''}
                  </TabsTrigger>
                  <TabsTrigger value="unassigned" className="flex-1">
                    Unassigned{unassigned.length ? ` (${unassigned.length})` : ''}
                  </TabsTrigger>
                </TabsList>
                <TabsContent value="techs" className="mt-2 rounded-lg border">
                  {isToday && fleet.isLoading ? (
                    <div className="space-y-2 p-2">
                      {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-14 w-full" />)}
                    </div>
                  ) : (
                    <TechList
                      entries={visibleEntries}
                      selectedId={selectedEntry?.tech.tech_id ?? null}
                      timezone={timezone}
                      onSelect={selectTech}
                    />
                  )}
                </TabsContent>
                <TabsContent value="unassigned" className="mt-2 rounded-lg border">
                  {isLoading ? (
                    <div className="space-y-2 p-2">
                      {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-14 w-full" />)}
                    </div>
                  ) : (
                    <UnassignedList
                      jobs={unassigned}
                      entries={visibleEntries}
                      unit={unit}
                      onSelectJob={selectJob}
                      onAssign={assign}
                    />
                  )}
                </TabsContent>
              </Tabs>
              {!isToday && (
                <p className="text-xs text-muted-foreground">
                  Live positions only show for today.{isAdmin ? ' Pick a technician to see the trail they drove on this day.' : ''}
                </p>
              )}
            </div>
          </div>
        )}

        <MoveConfirmDialog
          move={pendingMove}
          timezone={timezone}
          techName={techName}
          pending={move.isPending}
          onConfirm={confirmMove}
          onCancel={() => setPendingMove(null)}
        />
        <JobQuickViewSheet
          jobId={openJobId}
          open={!!openJobId}
          onOpenChange={(open) => !open && setOpenJobId(null)}
        />
      </div>
    </Layout>
  );
}

